import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Search, CheckCircle2, XCircle, QrCode, FileText, ExternalLink } from "lucide-react";

const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Card = ({ title, accent, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b" style={{ borderColor: accent + "25" }}>
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

/**
 * Public Verification Centre™ — Phase XVI
 * PUBLIC (no auth). Exposes only authenticity, never internal government data.
 */
export const PublicVerification = () => {
  const [docId, setDocId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/cadastre360/verify/catalog`);
        setCatalog(r.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const verify = async () => {
    if (!docId.trim()) { toast.error("Please enter a document ID"); return; }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/cadastre360/verify/${encodeURIComponent(docId.trim())}`);
      setResult(r.data);
      if (r.data.verified) toast.success("Document authenticity confirmed");
      else toast.error("Document not found in registry");
    } catch (e) { toast.error("Verification failed — " + e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen p-8" style={{ background: "linear-gradient(180deg,#071B2B 0%,#0B3558 100%)" }} data-testid="page-public-verify">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="rounded-sm border p-6 flex items-start gap-4"
             style={{ borderColor: GOLD + "60",
                       background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${GOLD}30 100%)` }}>
          <ShieldCheck size={38} className="mt-1" style={{ color: GOLD }} />
          <div>
            <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: GOLD }}>
              Cadastre360™ · Phase XVI · Public Verification Centre™
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
              Sovereign Document Authenticity Verification
            </h1>
            <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-3xl leading-relaxed">
              Validate the authenticity of Cabinet briefs, decisions, national value reports, executive reports and
              certificates issued by the Cadastre360™ Sovereign Trust Framework. This portal exposes ONLY authenticity —
              never internal government data.
            </div>
          </div>
        </div>

        <Card title="Verify a Document" accent={GOLD} testId="verify-form">
          <div className="flex flex-wrap gap-2">
            <input value={docId} onChange={(e) => setDocId(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && verify()}
                   placeholder="Enter document ID (e.g. brief-xxxxxxx, nvr-xxxxxxx, nar-xxxxxxx)"
                   className="flex-1 min-w-[300px] rounded-sm border bg-black/30 px-3 py-2 text-[13px] text-[#F6F8FA] border-white/15 font-mono"
                   data-testid="verify-input" />
            <button onClick={verify} disabled={busy}
                    className="rounded-sm border px-4 py-2 text-[12px] font-medium"
                    style={{ borderColor: GOLD + "60", color: "#F6F8FA", background: GOLD + "25" }}
                    data-testid="verify-button">
              <Search size={12} className="inline mr-1" /> Verify
            </button>
          </div>
        </Card>

        {result && (
          <Card title={result.verified ? "Verification result · AUTHENTIC" : "Verification result · NOT FOUND"}
                accent={result.verified ? GREEN : RED} testId="verify-result">
            <div className="flex items-center gap-3 mb-3">
              {result.verified
                ? <CheckCircle2 size={30} style={{ color: GREEN }} />
                : <XCircle size={30} style={{ color: RED }} />}
              <div>
                <div className="text-[16px] font-bold" style={{ color: result.verified ? GREEN : RED }}>
                  {result.authenticity_indicator || result.verification_status}
                </div>
                <div className="text-[11px] text-[#7C93A5] font-mono">{result.document_id}</div>
              </div>
            </div>
            {result.verified && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Document type</div>
                  <div className="text-[#F6F8FA]">{result.document_type}</div>
                </div>
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Issue authority</div>
                  <div className="text-[#F6F8FA]">{result.issue_authority}</div>
                </div>
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Timestamp</div>
                  <div className="text-[#F6F8FA]">{result.timestamp}</div>
                </div>
                <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Version</div>
                  <div className="text-[#F6F8FA]">{result.version}</div>
                </div>
                <div className="rounded-sm border px-2 py-1.5 md:col-span-2" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Digital signature (SHA-256)</div>
                  <div className="text-[#F6F8FA] font-mono text-[11px] break-all">{result.digital_signature_sha256}</div>
                </div>
                <div className="rounded-sm border px-2 py-1.5 md:col-span-2" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Certificate chain</div>
                  <div className="text-[#F6F8FA]">{result.certificate_chain?.join(" → ")}</div>
                </div>
              </div>
            )}
            <div className="mt-3 text-[10.5px] text-[#7C93A5] italic">{result.notice}</div>
          </Card>
        )}

        {catalog && (
          <Card title="Verifiable Document Types" accent={PURPLE} testId="verify-catalog">
            <div className="text-[11.5px] text-[#DCE3E8] mb-2">{catalog.notice}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
              {catalog.verifiable_documents.map((d) => (
                <div key={d} className="rounded-sm border px-2 py-1 flex items-center gap-2"
                     style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.45)" }}>
                  <FileText size={11} style={{ color: PURPLE }} />
                  <span className="text-[11.5px] text-[#F6F8FA]">{d.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1 text-center">
          Cadastre360™ · Sovereign Digital Trust Framework · Public Verification
        </div>
      </div>
    </div>
  );
};

export default PublicVerification;
